import { CommerceCloudOrder } from "./commerce-cloud-order";
import { Coverage } from "./coverage";
import { ExchangeOrderPrefixTypes } from "./exchange";
import { OrderRefundEvent, ProviderPrefixedOrderId } from "./order-schema";
import { MaybeOriginOrder } from "./origin-order-utils";
import { Parcel, ShipmentRatesOld } from "./outbound-labels";
import { InputAnswer, MultipleChoiceAnswer } from "./return-flow";
import { SesEvent } from "./ses-events";
import { Label, Rate } from "./shipment";
import {
  LineItem as LineItemZod,
  OrderFulfillmentStatus,
  OrderId,
  OriginAddress,
  LineItem as ShopifyLineItem,
} from "./shopify-order";
import { Team } from "./team";
import { TimelineEvent } from "./timeline";
import { ExternalShipmentTracker, ITrackable } from "./trackable";

export interface DiscountCode {
  code: string;
  amount: string;
  type: string;
}

// Keep this in sync with the YofiScore type in order-schema.ts

export interface YofiIndicator {
  name?: string | null;
  is_risk?: boolean | null;
}

export interface YofiScore {
  prediction_name: string;
  prediction_value: YofiRiskLevels;
  justification?: string | null;
  indicators?: YofiIndicator[] | null;
}

export enum OrderTrackingBillingStatus {
  BILLED = "billed",
  FREE = "free",
}

export interface Order extends ITrackable {
  coverage: Coverage;
  protected: boolean;
  redoPrice?: string;
  packageProtected: boolean;
  finalSaleReturnsProtected: boolean;
  credits: number;
  isExchangeOrder: boolean;
  coverageEnabled: boolean;
  _id: string;
  shopify_id: ProviderPrefixedOrderId;
  extendedWarranties: any[];
  team: string;
  createdAt: string;
  customer_name: string;
  lineItemsFulfillment: any[];
  finalSaleLineItemsProtection: FinalSaleLineItemProtection[];
  lineItems?: any[];
  stalledInFulfillment?: boolean;
  discount?: {
    id: string;
    description: string;
    code: string;
    expirationDateTime: string;
  };
  shopify: {
    id: OrderId;
    line_items: LineItem[];
    fulfillments: Fulfillment[];
    [key: string]: any;
    total_price: string;
    total_tax: string;
    financial_status: string;
    source_name: string;
    discount_codes: DiscountCode[];
    taxes_included: boolean;
    fulfillment_status: OrderFulfillmentStatus | null | undefined;
    cancelled_at: string | null | undefined;
    total_shipping_price_set: {
      shop_money: { amount: string };
      presentment_money: { amount: string };
    };
  };
  shopifyCreatedAt: string;
  shopifyUpdatedAt: string;
  timeline: TimelineEvent[];
  refundEvents?: OrderRefundEvent[];
  updatedAt: string;
  originItems?: OriginItem[];
  currentEmailFlows?: {
    emailFlowId: string;
    currentStep: number;
    continueDate: string;
    fulfillmentId?: string;
  }[];
  trackers: { _tracker: ExternalShipmentTracker; fulfillmentID: string }[];
  trackingAnalytics: {
    email: SesEvent[];
    page: {
      url: string;
      eventType: "ad" | "upsell" | "promotion" | "subscription";
      image?: string;
      createdAt: string;
    }[];
  };
  trackingTextsSent?: { sid: string; mms: boolean; sentAt: string }[];
  trackingEmailsSent?: {
    emailId: string;
    status?: string;
    sentAt: string;
    s3URL?: string;
    trigger?: string;
    trackingCode?: string;
  }[];
  trackingBillingStatus?: OrderTrackingBillingStatus;
  trackingBillingEligibleAt?: Date;
  attributedToRecoverAt?: Date;
  yofiScores?: Record<string, YofiScore>;
  shipmentRates?: ShipmentRatesOld;
  parcels?: Parcel[];
  labels?: { label: Label; rate: Rate; printed: boolean }[];
  provider?: Provider;
  conciergeAssistedDate?: Date;
  originOrder: MaybeOriginOrder;
  tnplContract?: string;
  parentOrderId?: string;
  buyerId?: string;
  coverageAnalytics?: { redoOptedInFromCart?: boolean };
  commercecloud?: CommerceCloudOrder;
}

export interface OrderWithReturnableItems extends Order {
  hasReturnableItems?: boolean;
  line_items?: LineItem[];
}

// If we add to here, please also add to getProviderEnabledTeams in server/src/utils/coverage-monitor.ts
export enum Provider {
  COMMENTSOLD = "commentsold",
  SHOPIFY = "shopify",
  COMMERCE_CLOUD = "commerce-cloud",
  BIGCOMMERCE = "bigcommerce",
  NETSUITE = "netsuite",
  MANUAL = "manual",
  TIKTOK = "tiktok",
  AMAZON = "amazon",
  WALMART = "walmart",
  EBAY = "ebay",
  APPAREL_MAGIC = "apparel-magic",
  REDO_MARKETPLACE = "redo-marketplace",
  SFOM = "sfom",
  AIMS360 = "aims360",
  CIN7_OMNI = "cin7-omni",
  CIN7_CORE = "cin7-core",
  REDO_API = "redo-api", // See the OrdersApi integration (used by Porsche)
  CUSTOM_RETURN_INTEGRATION = "custom-return-integration",
  REDO = "redo", // Redo native checkout (agentic commerce)
  FAIRE = "faire",
  WAYFAIR = "wayfair",
  BRIGHTPEARL = "brightpearl",
}

export function getProviderDisplayName(provider: Provider): string {
  const map: Record<Provider, string> = {
    [Provider.COMMENTSOLD]: "CommentSold",
    [Provider.SHOPIFY]: "Shopify",
    [Provider.COMMERCE_CLOUD]: "Commerce Cloud",
    [Provider.BIGCOMMERCE]: "BigCommerce",
    [Provider.NETSUITE]: "NetSuite",
    [Provider.MANUAL]: "Manual",
    [Provider.TIKTOK]: "TikTok",
    [Provider.AMAZON]: "Amazon",
    [Provider.WALMART]: "Walmart",
    [Provider.EBAY]: "eBay",
    [Provider.APPAREL_MAGIC]: "Apparel Magic",
    [Provider.REDO_MARKETPLACE]: "Redo Marketplace",
    [Provider.SFOM]: "SFOM",
    [Provider.AIMS360]: "AIMS360",
    [Provider.CIN7_OMNI]: "Cin7 Omni",
    [Provider.CIN7_CORE]: "Cin7 Core",
    [Provider.REDO_API]: "Redo API",
    [Provider.CUSTOM_RETURN_INTEGRATION]: "Custom Return Integration",
    [Provider.REDO]: "Redo",
    [Provider.FAIRE]: "Faire",
    [Provider.WAYFAIR]: "Wayfair",
    [Provider.BRIGHTPEARL]: "Brightpearl",
  };
  return map[provider];
}

// Default source names for orders from different platforms. This is used for the coverage monitor, it should not be used for any other purpose. Use the Provider enum.
export enum DefaultOrderSource {
  SHOPIFY_WEB = "web",
  BIGCOMMERCE = "bigcommerce",
  COMMENTSOLD = "CommentSold",
  COMMERCECLOUD = "Commerce Cloud",
  SFOM = "Salesforce Order Management",
  CUSTOM_RETURN_INTEGRATION = "Custom Return Integration",
}

export const orderProviderToLabel: Record<Provider, string> = {
  [Provider.SHOPIFY]: "Shopify",
  [Provider.COMMENTSOLD]: "CommentSold",
  [Provider.COMMERCE_CLOUD]: "Commerce Cloud",
  [Provider.BIGCOMMERCE]: "BigCommerce",
  [Provider.MANUAL]: "Manual",
  [Provider.NETSUITE]: "NetSuite",
  [Provider.TIKTOK]: "TikTok",
  [Provider.AMAZON]: "Amazon",
  [Provider.WALMART]: "Walmart",
  [Provider.EBAY]: "eBay",
  [Provider.APPAREL_MAGIC]: "Apparel Magic",
  [Provider.REDO_MARKETPLACE]: "Redo Marketplace",
  [Provider.SFOM]: "SFOM",
  [Provider.AIMS360]: "AIMS360",
  [Provider.CIN7_OMNI]: "Cin7 Omni",
  [Provider.CIN7_CORE]: "Cin7 Core",
  [Provider.REDO_API]: "Redo API",
  [Provider.CUSTOM_RETURN_INTEGRATION]: "Custom Return Integration",
  [Provider.REDO]: "Redo",
  [Provider.FAIRE]: "Faire",
  [Provider.WAYFAIR]: "Wayfair",
  [Provider.BRIGHTPEARL]: "Brightpearl",
};

export const orderProviderToOrderPrefix: Record<Provider, string> = {
  [Provider.SHOPIFY]: "",
  [Provider.COMMENTSOLD]: "CS-",
  [Provider.COMMERCE_CLOUD]: "CC-",
  [Provider.BIGCOMMERCE]: "BC-",
  [Provider.MANUAL]: "",
  [Provider.NETSUITE]: "NS-",
  [Provider.TIKTOK]: "",
  [Provider.AMAZON]: "",
  [Provider.WALMART]: "",
  [Provider.EBAY]: "",
  [Provider.APPAREL_MAGIC]: "",
  [Provider.REDO_MARKETPLACE]: "",
  [Provider.SFOM]: "SFOM-",
  [Provider.AIMS360]: "",
  [Provider.CIN7_OMNI]: "",
  [Provider.CIN7_CORE]: "",
  [Provider.REDO_API]: "API-",
  [Provider.CUSTOM_RETURN_INTEGRATION]: "CUSTOM-",
  [Provider.REDO]: "REDO-",
  [Provider.FAIRE]: "",
  [Provider.WAYFAIR]: "WF-",
  [Provider.BRIGHTPEARL]: "BP-",
};

export enum OrderRefundEventType {
  SHIPPING = "shipping",
}

export enum YofiRiskLevels {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
}

export interface OrdersFacets {
  total: number;
}

export interface TaxLine {
  price: string;
  rate: number;
  title: string;
}

export interface LineItem {
  id: number;
  product_id: number | null;
  sku: string;
  properties: { name: string; value: string }[];
  title: string;
  variant_title: string;
  variant_id: number;
  [key: string]: any;
  tax_lines: TaxLine[];
  discount_price: string | null;
  greenReturn?: boolean;
  inputAnswers?: InputAnswer[];
  merchantAddress?: any;
  multipleChoiceAnswers?: MultipleChoiceAnswer[];
  reason: string | null;
  selectedReturnType?: string;
  tags?: string[];
}

export interface OriginItem {
  unitPrice: string;
  preDiscountPrice?: string;
  currency: string;
  tax: string;
  originVariantId: number | string;
  newVariantId: number | string;
  applicableShopifyOrder?: number;
}

export interface Tracker {
  tracking_code: string;
  carrier: string;
  status: string;
  est_delivery_date: string;
  public_url?: string;
  created_at: string;
  updated_at: string;
  tracking_details: { message: string; status: string; datetime: string }[];
}

export interface Fulfillment {
  id: number;
  name: string;
  status: string;
  tracking_company: string | null;
  tracking_url: string | null;
  shipment_status: string | null;
  // If multiple tracking numbers are set on this fulfillment, only the first one will be returned in the tracking_number field.
  tracking_number: string;
  tracking_numbers: string[];
  tracking_urls: string[];
  line_items: LineItem[];
  created_at: string;
  updated_at?: string;
  location_id?: string;
  origin_address?: OriginAddress;
}

export interface ShopifyAddress {
  name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  province?: string | null;
  province_code?: string | null;
  country?: string | null;
  country_code?: string | null;
  zip?: string | null;
  phone?: string | null;
  company?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

// Only final sale protection is based on the line item, not the whole cart like
// package protection or returns coverage
export interface FinalSaleLineItemProtection {
  shopify_line_item_id: string;
  finalSaleReturnsProtected: boolean;
}

export function isLineItemFinalSaleProtected(
  lineItem: LineItem | ShopifyLineItem | undefined,
  finalSaleLineItemsProtection: FinalSaleLineItemProtection[] | undefined,
): boolean {
  const finalSaleLineItemProtection = finalSaleLineItemsProtection?.find(
    (protection) => protection.shopify_line_item_id === lineItem?.id.toString(),
  );
  return !!(
    lineItem?.finalSaleProtected ||
    (finalSaleLineItemProtection?.finalSaleReturnsProtected ?? false)
  );
}

export const DEFAULT_DUPLICATE_ORDER_NUMBER_FORMAT = " ({n})";

export class ExchangeOrderFormat {
  constructor(private readonly settings: Team["settings"]["exchanges"]) {}

  private getAffixRegexString(
    prefix?: string,
    suffix?: string,
  ): string | undefined {
    if (!prefix && !suffix) {
      return undefined;
    }

    return `${prefix ?? ""}.*${suffix ?? ""}`;
  }

  private getAffixedString(
    original: string,
    prefix?: string,
    suffix?: string,
  ): string {
    return (prefix ?? "") + original + (suffix ?? "");
  }

  getName(original: string, prefixType: ExchangeOrderPrefixTypes) {
    if (this.settings.orderName.useShopifyGeneratedName) {
      return null;
    }

    // Strip # from original attribute
    // EX: Manly Bands Merchant
    if (this.settings.shouldStripHash) {
      original = original.replace("#", "");
    }

    if (this.settings.suffix !== null) {
      return this.settings.suffix
        ? `${original}-${this.settings.suffix}`
        : original;
    }

    const typedOrderName = this.settings.orderName.orderNamesByReturnType.find(
      (typedOrderName) => typedOrderName.returnType === prefixType,
    );

    if (typedOrderName?.prefix || typedOrderName?.suffix) {
      return this.getAffixedString(
        original,
        typedOrderName?.prefix,
        typedOrderName?.suffix,
      );
    }

    return this.getAffixedString(
      original,
      this.settings.orderName.prefix,
      this.settings.orderName.suffix,
    );
  }

  /**
   * Formats the duplicate order number suffix.
   * @param baseName - The base order name (e.g., "ORDER-EXC")
   * @param count - The duplicate number (e.g., 2 for the second exchange), must be an integer >= 2
   * @returns The formatted name (e.g., "ORDER-EXC (2)" or "ORDER-EXC-2")
   */
  formatDuplicateNumber(baseName: string, count: number): string {
    if (count < 2 || !Number.isInteger(count)) {
      throw new Error("count must be an integer >= 2");
    }
    const format =
      this.settings.orderName.duplicateOrderNumberFormat ??
      DEFAULT_DUPLICATE_ORDER_NUMBER_FORMAT;
    return baseName + format.replace("{n}", String(count));
  }

  /**
   * Strips the duplicate number suffix from an order name.
   * Handles both default format " (n)" and custom formats like "-n".
   * @param name - The order name that may contain a duplicate suffix
   * @returns The name without the duplicate suffix
   */
  stripDuplicateNumber(name: string): string {
    const format =
      this.settings.orderName.duplicateOrderNumberFormat ??
      DEFAULT_DUPLICATE_ORDER_NUMBER_FORMAT;
    if (!format.includes("{n}")) {
      throw new Error(
        "duplicateOrderNumberFormat must contain {n} placeholder",
      );
    }
    // Build regex from the format by escaping special chars and replacing {n} with a number pattern
    const escapedFormat = format
      .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
      .replace(/\\\{n\\\}/g, "\\d+");
    const regex = new RegExp(`${escapedFormat}$`);
    return name.replace(regex, "");
  }

  /**
   * Computes the base exchange order name for a given original order name.
   * Preserves the original order name verbatim when it is a raw merchant
   * order. Only strips a duplicate counter suffix if the original name was
   * itself generated by our exchange naming logic (chained exchange).
   *
   * This avoids the class of bugs where legitimate "-digits" in an original
   * order name (e.g. Shopify order "SP-2") gets misread as a duplicate
   * counter and stripped before re-applying the exchange affix.
   */
  getBaseExchangeOrderName(
    original: string,
    prefixType: ExchangeOrderPrefixTypes,
    options?: { sourceOrderIsExchangeOrder?: boolean },
  ): string | null {
    if (this.settings.orderName.useShopifyGeneratedName) {
      return null;
    }

    if (options?.sourceOrderIsExchangeOrder) {
      const stripped = this.stripDuplicateNumber(original);
      if (stripped !== original && this.isName(stripped)) {
        return stripped;
      }
      if (this.isName(original)) {
        return original;
      }
    }

    return this.getName(original, prefixType);
  }

  // Since isName is testing for any type of prefix and suffix, it will result in incrementing the exchange number even if the new type is different from the current type
  // ie. 1234-EXC -> 1234-EXC (2) instead of 1234-EXC -> 1234-EXC-WTY
  // The alternative that we think could work is 1234-EXC -> 1234-WTY (2), if the second exchange is a different type
  isName(name: string) {
    if (this.settings.suffix !== null) {
      return (
        !!this.settings.suffix && name.endsWith(`-${this.settings.suffix}`)
      );
    }

    const affixPairs = [
      {
        prefix: this.settings.orderName.prefix,
        suffix: this.settings.orderName.suffix,
      },
    ];

    for (const typedOrderName of this.settings.orderName
      .orderNamesByReturnType) {
      affixPairs.push({
        prefix: typedOrderName.prefix,
        suffix: typedOrderName.suffix,
      });
    }

    const regexString = affixPairs
      .map((fixPair) =>
        this.getAffixRegexString(fixPair.prefix, fixPair.suffix),
      )
      .filter(Boolean)
      .reduce((acc, regex) => `${acc ? `${acc}|` : ""}${regex}`, "");

    const regex = new RegExp(`^(${regexString})$`);
    return regex.test(name);
  }
}

interface CheckIsRedoLineItem {
  sku: string | undefined;
  vendor: string | undefined;
  name: string | undefined;
}

export const isRedoLineItem = (
  lineItem:
    | LineItem
    | ShopifyLineItem
    | CheckIsRedoLineItem
    | LineItemZod
    | undefined,
) => {
  if (
    /^redo$|^re:do$/i.test(lineItem?.vendor) || // milwaukeemotorcycleclothing changed the vendor :/
    /route-/i.test(lineItem?.sku || "") ||
    /routeins/i.test(lineItem?.sku || "") ||
    /insurance|package\s+protection|order\s+protection/i.test(lineItem?.name)
  ) {
    return true;
  }
  return false;
};

export const getNumNonRedoItemsInOrders = (orders: Order[]) => {
  // Exclude Redo items
  return orders.reduce((acc, order) => {
    return (
      acc +
      order.shopify.line_items
        ?.filter((lineItem) => !isRedoLineItem(lineItem))
        .reduce((acc, lineItem) => acc + lineItem.quantity, 0)
    );
  }, 0);
};

const orderIdPrefix: Record<Provider, string> = {
  [Provider.SHOPIFY]: "",
  [Provider.COMMENTSOLD]: "cs:",
  [Provider.COMMERCE_CLOUD]: "",
  [Provider.BIGCOMMERCE]: "bc:",
  [Provider.NETSUITE]: "ns:",
  [Provider.MANUAL]: "",
  [Provider.AMAZON]: "",
  [Provider.TIKTOK]: "",
  [Provider.WALMART]: "",
  [Provider.EBAY]: "",
  [Provider.APPAREL_MAGIC]: "",
  [Provider.REDO_MARKETPLACE]: "",
  [Provider.SFOM]: "",
  [Provider.AIMS360]: "",
  [Provider.CIN7_OMNI]: "",
  [Provider.CIN7_CORE]: "",
  [Provider.REDO_API]: "API-",
  [Provider.CUSTOM_RETURN_INTEGRATION]: "",
  [Provider.REDO]: "redo:",
  [Provider.FAIRE]: "",
  [Provider.WAYFAIR]: "",
  [Provider.BRIGHTPEARL]: "",
};

function isProviderPrefixedOrderId(
  orderId: OrderId,
  provider: Provider,
): boolean {
  return orderId.toString().startsWith(orderIdPrefix[provider]);
}

export function orderIdToProviderPrefixedOrderId(
  provider: Provider,
  orderId: OrderId,
): ProviderPrefixedOrderId {
  if (isProviderPrefixedOrderId(orderId, provider)) {
    return orderId as ProviderPrefixedOrderId;
  }
  return `${orderIdPrefix[provider]}${orderId}` as ProviderPrefixedOrderId;
}

export function providerPrefixedOrderIdToOrderId(
  provider: Provider,
  orderId: ProviderPrefixedOrderId,
): OrderId {
  if (!isProviderPrefixedOrderId(orderId, provider)) {
    return orderId as OrderId;
  }
  return orderId.replace(orderIdPrefix[provider], "") as OrderId;
}
